export type Experience = {
    period: string
    role: string
    company: string
    points: string[]
}

export type Milestone = {
    year: string
    category: string
    title: string
}

export type ProcessStep = {
    number: string
    title: string
    duration: string
    text: string
}

export type Principle = {
    number: string
    title: string
    text: string
}

export const aboutIntro = "I'm Zaki, a software engineer from Padang, Indonesia. I build websites, web applications and real-time GIS systems, and I like the hard part in the middle where performance, data and people meet."

export const aboutStory = "I studied Informatics Engineering at Padang State University and graduated with a 3.60 GPA. Growing up on a coast that lives with tsunami risk shaped my thesis: Padang Rescue, an early warning and evacuation system that delivers alerts in 37 ms and reroutes people when shelters fill up. Along the way I have built a Next.js platform for international medical services, a ticketing frontend for a live events company, corporate websites in WordPress, and spatial data for a plantation WebGIS during my internship. I work in English and Indonesian, and I enjoy working closely with the people I build for."

export const experience: Experience[] = [
    {
        period: "Sep 2026 to Oct 2026",
        role: "Frontend Developer",
        company: "Seven Star Production",
        points: [
            "Engineered a responsive online ticketing frontend with TypeScript, Next.js and Tailwind CSS.",
            "Designed a minimal, modern interface that streamlined the ticket purchasing process."
        ]
    },
    {
        period: "Jan 2026 to Sep 2026",
        role: "Software Engineer",
        company: "Padang Rescue",
        points: [
            "Built a real-time tsunami mitigation mobile GIS system in React Native.",
            "Cut alert latency from 677.57 ms to 37.39 ms with persistent WebSocket connections.",
            "Extended Dijkstra routing with Failover Node Elimination to reroute users in 0.4066 ms."
        ]
    },
    {
        period: "Aug 2025 to Oct 2025",
        role: "WebGIS Intern",
        company: "PT Mitra Tech Indonesia",
        points: [
            "Processed and cleaned spatial data from SHP, CAD and Excel files in ArcGIS Pro.",
            "Contributed to a WebGIS spatial asset management system for the plantation industry."
        ]
    },
    {
        period: "Jun 2025 to Jul 2025",
        role: "Web Developer",
        company: "YPRO",
        points: [
            "Developed a corporate website with WordPress and Divi Builder.",
            "Worked directly with the client to align design and content with business needs."
        ]
    },
    {
        period: "Aug 2023 to Sep 2023",
        role: "Web Developer",
        company: "Seven Star Production",
        points: [
            "Improved the look and usability of the company website in WordPress."
        ]
    },
    {
        period: "Apr 2023 to Jun 2023",
        role: "Fullstack Developer",
        company: "AssistCare Indonesia",
        points: [
            "Led the architecture and development of a Next.js platform for international medical services.",
            "Improved operational performance by 40% and doubled processing speed."
        ]
    }
]

export const milestones: Milestone[] = [
    { year: "2026", category: "Degree", title: "Bachelor of Informatics Engineering, Padang State University, GPA 3.60 out of 4.00" },
    { year: "2026", category: "Research paper", title: "Multi-platform tsunami early warning and evacuation system using Ray Casting and Dijkstra in Padang City" },
    { year: "2025", category: "Field experience", title: "Industrial Field Experience (PLI) at PT Mitra Tech Indonesia" },
    { year: "2024", category: "Certification", title: "MikroTik Certified Network Associate (MTCNA)" },
    { year: "2024", category: "Course", title: "HCIA-AI V3.5, Huawei Talent Online" }
]

export const processIntro = "Every project moves through the same four steps. How long each one takes changes. The order does not."

export const processSteps: ProcessStep[] = [
    {
        number: "01.",
        title: "Understand",
        duration: "First conversation",
        text: "I start with the people, not the stack: who will use this, what they need to get done and what success looks like for the business."
    },
    {
        number: "02.",
        title: "Plan",
        duration: "Week one",
        text: "I write down the scope, the screens and the technology on a single page, so every decision after it has something clear to answer to."
    },
    {
        number: "03.",
        title: "Build",
        duration: "Weeks two onwards",
        text: "I build in small, working pieces and share them early. You see real progress in the browser every week instead of a big reveal at the end."
    },
    {
        number: "04.",
        title: "Ship",
        duration: "Launch and after",
        text: "I deploy, test on real devices, measure performance and stay close after launch to fix what the real world finds."
    }
]

export const principles: Principle[] = [
    {
        number: "i.",
        title: "Clarity",
        text: "Simple code and plain explanations. If I cannot explain a technical choice in one sentence, it is probably the wrong one."
    },
    {
        number: "ii.",
        title: "Speed",
        text: "Fast pages, fast alerts, fast replies. Performance is measured from the start, not patched in at the end."
    },
    {
        number: "iii.",
        title: "Reliability",
        text: "Software should keep working when conditions are bad. I plan for weak signals, full servers and unexpected input."
    },
    {
        number: "iv.",
        title: "Curiosity",
        text: "From GIS to networking to AI, I keep learning outside my comfort zone and bring what I learn back to the work."
    }
]
